import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import Linkify from 'react-linkify';
import { connect } from 'react-redux';
import { MerchantBookingStatus } from '../../../../../interfaces/merchants/bookings';
import * as I from '../../../../../interfaces/merchants/resources/books';
import Button, { ButtonSize } from '../../../../../atoms/buttons/Button';
import StringUtils from '../../../../../components/utils/StringUtils';
import BookingWithPageDetailImage from '../../../../../molecules/merchant/resources/book/BookingWithPageDetailImage';

import css from './BookingErrorPage.module.scss';
import dynamic from 'next/dynamic';

const RichViewer = dynamic(
  () => import('../../../../../components/RichViewer'),
  {
    ssr: false,
  },
);

interface StateProps {
  nickname: string;
  name: string;
  status: MerchantBookingStatus;
  coverContentsType: string;
  primaryImage?: {
    meta: {
      width: number;
      height: number;
    };
    thumb?: {
      url: string;
    };
  };
  coverContent?: {
    url: string;
    embed: string;
    thumbnailUrl: string;
  };
  isJsonDescription: boolean;
  description?: string;
  descriptionText?: string;
}

interface Props extends StateProps {
  withExpressFlow: boolean;
}

const BookingErrorPage: React.FC<Props> = (props) => {
  const intl = useIntl();
  const errorMessage = useCallback<(status: MerchantBookingStatus) => string>(
    (status) => {
      switch (status) {
        case MerchantBookingStatus.Closed: {
          return intl.formatMessage({
            id: 'books.bookingErrorPage.errorMessage.closed',
          });
        }
        case MerchantBookingStatus.FullyBooked:
          return intl.formatMessage({
            id: 'books.bookingErrorPage.errorMessage.fullyBooked',
          });
        default:
          return '';
      }
    },
    [intl],
  );

  return (
    <div className={css.BookingErrorPage}>
      <div className={css.BookingErrorPage__ErrorMessage}>
        {errorMessage(props.status)}
      </div>
      <div className={css.BookingErrorPage__ContentSection}>
        {props.withExpressFlow && (
          <>
            <div className={css.BookingErrorPage__Heading}>
              <div className={css.BookingErrorPage__Title}>{props.name}</div>
            </div>
            <div className={css.BookingErrorPage__Thumbnail}>
              {props.coverContentsType === 'upload_image' &&
                props.primaryImage && (
                  <BookingWithPageDetailImage
                    name={props.name}
                    primaryImage={props.primaryImage}
                  />
                )}
              {props.coverContentsType === 'embed' &&
                props.coverContent &&
                props.coverContent.embed && (
                  <div
                    className={css.BookingErrorPage__Embed}
                    dangerouslySetInnerHTML={{
                      __html: props.coverContent.embed,
                    }}
                  />
                )}
            </div>
            <div className={css.BookingErrorPage__Description}>
              {props.isJsonDescription && props.description ? (
                <RichViewer value={props.description} />
              ) : (
                props.descriptionText && (
                  <Linkify>
                    {StringUtils.simpleFormat(props.descriptionText)}
                  </Linkify>
                )
              )}
            </div>
          </>
        )}
        {!props.withExpressFlow && (
          <div className={css.BookingErrorPage__ButtonSection}>
            <Button
              isGhost={true}
              isFull={true}
              buttonSize={ButtonSize.Medium}
              href={`/${props.nickname}`}
            >
              {intl.formatMessage({
                id: 'books.bookingErrorPage.backToMainPage',
              })}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

const mapStateProps = ({
  merchantsResourcesBooks,
}: {
  merchantsResourcesBooks: I.State;
}): StateProps => ({
  ...merchantsResourcesBooks.merchantResource!,
});

export default connect(mapStateProps)(BookingErrorPage);
